import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UtensilsCrossed, Heart, CheckCircle2, Star, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { Spinner } from "../components/common";

const STATE_META = {
  want: { label: "Want to try", cls: "bg-rose-50 text-rose-700 border-rose-300", icon: Heart },
  ate: { label: "Ate here", cls: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
  favorite: { label: "Favorite", cls: "bg-amber-50 text-amber-700 border-amber-300", icon: Star },
};

/* Public read-only view of a companion's shared food list. */
export default function SharedFoodList() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/food-list/shared/${token}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-16 text-center" data-testid="shared-foodlist-unavailable">
        <UtensilsCrossed className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
        <h1 className="font-heading font-900 text-2xl mb-2">This shared food list is unavailable</h1>
        <p className="text-sm text-muted-foreground">The owner may have turned sharing off. Ask them for a fresh link.</p>
      </div>
    );
  }
  if (!data) return <Spinner full />;

  const all = [...(data.items || []), ...(data.guide_items || [])];

  const Badge = ({ state }) => {
    const m = STATE_META[state];
    if (!m) return null;
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] font-700 px-1.5 py-0.5 border rounded-full ${m.cls}`}>
        <m.icon className="w-3 h-3" /> {m.label}
      </span>
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10" data-testid="shared-foodlist-page">
      <h1 className="font-heading font-900 text-3xl flex items-center gap-3 mb-1">
        <UtensilsCrossed className="w-8 h-8 text-orange-600" /> {data.owner_name}&rsquo;s Food List
      </h1>
      <p className="text-sm text-muted-foreground mb-8">
        Shared from TravelerPulse — places they want to try, ate at and loved. Plan your meals together.
      </p>

      {all.length === 0 ? (
        <p className="text-sm text-muted-foreground border border-dashed border-zinc-300 p-8 text-center" data-testid="shared-foodlist-empty">
          Nothing on this list yet.
        </p>
      ) : (
        <div className="space-y-2" data-testid="shared-foodlist-items">
          {all.map((m, i) => (
            <div key={m.id || m.place_id || i} className="border border-zinc-300 bg-white p-3.5" data-testid={`shared-foodlist-item-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-700">{m.name}</span>
                {m.destination && <span className="text-xs text-muted-foreground">{m.destination}{m.area ? ` · ${m.area}` : ""}</span>}
                <Badge state={m.state} />
                {m.url && (
                  <a href={m.url} target="_blank" rel="noreferrer" className="text-primary ml-auto" data-testid={`shared-foodlist-link-${i}`}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
              {m.notes && <p className="text-sm text-zinc-700 mt-1">{m.notes}</p>}
              {(m.photos || []).length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {m.photos.map((ph) => (
                    <img key={ph.id} src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}`} alt={ph.caption || ""}
                      className="w-20 h-20 object-cover border border-zinc-200" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 border-t border-zinc-200 pt-6 text-sm text-muted-foreground">
        Want your own food list?{" "}
        <Link to="/travel-hub" className="text-primary font-700 hover:underline" data-testid="shared-foodlist-cta">
          Build one free in My Travel Hub →
        </Link>
      </div>
    </div>
  );
}
